import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { format, parseISO } from "date-fns"; // For formatting custom string dates

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DistanceReportsTable from "./distancereporttable";

import DateRangePicker from "layouts/DateTimerange/dateTimeRange"; // ✅ Custom Range Picker Imported
import DhundhooLoader from "../Loader/Loader";

function DistanceReport() {
  const [distanceData, setDistanceData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [loading, setLoading] = useState(false);

  const getYesterdayDateString = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1); // 1 din subtract kiya
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const yesterdayStr = getYesterdayDateString();
  const defaultStart = `${yesterdayStr}T00:00`;
  const defaultEnd = `${yesterdayStr}T23:59`;

  // ✅ Set to null initially to show placeholder
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // 1. Aapke screenshot ke mutabiq charo columns define kiye hain
  const columns = [
    { Header: "Vehicle Number", accessor: "vehicle", width: "25%" },
    { Header: "From Date", accessor: "fromDate", width: "25%" },
    { Header: "To Date", accessor: "toDate", width: "25%" },
    { Header: "Total Distance (Km)", accessor: "distance", width: "25%" },
  ];

  const fetchLogs = async () => {
    // ✅ Validation: Dates must be selected
    if (!startTime || !endTime) {
      setErrorMessage("Please select a date range first.");
      openErrorSB();
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("token");

      // DateRangePicker gives "YYYY-MM-DDTHH:mm".
      // We parse it first, then format it to "YYYY/MM/DD 00:00" or "YYYY/MM/DD 23:59"
      const formatApiDate = (dateString, isEnd) => {
        const dateObj = parseISO(dateString);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        const time = isEnd ? "23:59" : "00:00";
        return `${year}-${month}-${day}`;
      };

      // Table Display format: Apr 01, 2026 00:00:00
      const displayFromDate = `${format(parseISO(startTime), "MMM dd, yyyy")} 00:00:00`;
      const displayToDate = `${format(parseISO(endTime), "MMM dd, yyyy")} 23:59:00`;

      const payload = {
        // timeZone: "Asia/Calcutta",
        startTime: formatApiDate(startTime, false),
        endTime: formatApiDate(endTime, true),
      };

      const config = {
        method: "post",
        // url: "https://v3.dhundhoo.com/reports/todayDistances",
        url: "https://v3.dhundhoo.com/reports/distanceReport ",
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data: payload,
      };

      const response = await axios.request(config);

      // 2. Map data to include selected dates in each row
     const formattedRows = response.data.map((item) => ({
        vehicle: item.thingName,           // Extracting thingName from the object
        fromDate: displayFromDate,         // Keeping your displayFromDate logic
        toDate: displayToDate,             // Keeping your displayToDate logic
        distance: typeof item.distance === "number" ? item.distance.toFixed(2) : item.distance,
      }));

      setDistanceData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error("API Error:", error);
      setErrorMessage("Error loading Distance report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (distanceData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }

      const formatFileDate = (dateTime) => {
  if (!dateTime) return "";

  const date = new Date(dateTime);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const formattedDate = `${formatFileDate(startTime)}_to_${formatFileDate(endTime)}`;

    // 1. Headers ko define karein
    const headers = ["Vehicle Number", "From Date", "To Date", "Total Distance (Km)"];

    // 2. Data rows ko format karein (Wrap values in quotes to handle commas)
    const rows = distanceData.map((row) => {
      return [
        `"${row.vehicle}"`,
        `"${row.fromDate}"`,
        `"${row.toDate}"`,
        `"${row.distance}"`
      ].join(","); // Columns ko comma se separate karein
    });

    // 3. BOM (Byte Order Mark) add karein taaki Excel special characters/formatting sahi dikhaye
    const csvContent = "\uFEFF" + [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `DistanceReport_${formattedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setStartTime(defaultStart); // ✅ Reset back to yesterday
    setEndTime(defaultEnd);
    setDistanceData([]);
    setErrorMessage("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">

                    {/* ✅ Custom DateRangePicker */}
                    <Grid item xs={12} md={6}>
                      <div style={{ width: '46%' }}>
                        <DateRangePicker
                          startTime={startTime}
                          endTime={endTime}
                          onChange={({ startTime: s, endTime: e }) => {
                            setStartTime(s);
                            setEndTime(e);
                          }}
                          disabled={loading}
                          style={{ Height: '10px' }}
                        />
                      </div>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1.5}>
                      <MDButton
                        fullWidth
                        variant="gradient"
                        color="warning" // Matching your previous styling
                        size="small"
                        onClick={fetchLogs}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1.5}>
                      <MDButton
                        fullWidth
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleClear}
                        disabled={loading}
                      >
                        CLEAR
                      </MDButton>
                    </Grid>
                    <Grid item xs={4} sm={2} md={1.5}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || distanceData.length === 0} />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <DhundhooLoader />
                ) : (
                  <DistanceReportsTable
                    table={{ columns, rows: distanceData }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color="success"
        icon="check"
        title="Distance Report"
        content="Data Loaded Successfully"
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Distance Report"
        content="Failed to load report."
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default DistanceReport;